import React, { FunctionComponent, useReducer, useEffect } from 'react';

import { IReducerAction } from '@dw/models/shared';
import { useMediaQuery, useTheme } from '@mui/material';
import { SCREEN_SIZE } from '@dw/constants';

type TScreenSizeContextActions = 'SET_SCREEN_SIZE';

interface IScreenSizeContext {
  currentSize: SCREEN_SIZE;
  isMobile: boolean;
  isTablet: boolean;
  isMobileOrTablet: boolean;
  isSmallDesktop: boolean;
  isLargeDesktop: boolean;
}

const initialState: IScreenSizeContext = {
  currentSize: SCREEN_SIZE.XS,
  isMobile: true,
  isTablet: false,
  isMobileOrTablet: true,
  isSmallDesktop: false,
  isLargeDesktop: false
};
export const ScreenSizeContext = React.createContext<IScreenSizeContext>(initialState);

const ScreenSizeContextReducer = (state: IScreenSizeContext, action: IReducerAction<TScreenSizeContextActions>): IScreenSizeContext => {
  switch (action.type) {
    case 'SET_SCREEN_SIZE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

interface IScreenSizeContextProviderProps {
    children: React.ReactNode;
}
export const ScreenSizeContextProvider: FunctionComponent<IScreenSizeContextProviderProps> = (props: IScreenSizeContextProviderProps) => {
    const [state, dispatch] = useReducer(ScreenSizeContextReducer, {...initialState});

    const theme = useTheme();
    const isXS = useMediaQuery(theme.breakpoints.only('xs'));
    const isSM = useMediaQuery(theme.breakpoints.only('sm'));
    const isMD = useMediaQuery(theme.breakpoints.only('md'));
    const isLG = useMediaQuery(theme.breakpoints.only('lg'));
    const isXL = useMediaQuery(theme.breakpoints.only('xl'));

    useEffect(() => {
      let updatedState: IScreenSizeContext | null = null;
      if (isXS && state.currentSize !== SCREEN_SIZE.XS) {
        updatedState = { currentSize: SCREEN_SIZE.XS, isMobile: true, isMobileOrTablet: true, isTablet: false, isSmallDesktop: false, isLargeDesktop: false };
      } else if (isSM && state.currentSize !== SCREEN_SIZE.SM) {
        updatedState = { currentSize: SCREEN_SIZE.SM, isMobile: false, isTablet: true, isMobileOrTablet: true, isSmallDesktop: false, isLargeDesktop: false };
      } else if (isMD && state.currentSize !== SCREEN_SIZE.MD) {
        updatedState = { currentSize: SCREEN_SIZE.MD, isMobile: false, isTablet: false, isMobileOrTablet: false, isSmallDesktop: true, isLargeDesktop: false };
      } else if (isLG && state.currentSize !== SCREEN_SIZE.LG) {
        updatedState = { currentSize: SCREEN_SIZE.LG, isMobile: false, isTablet: false, isMobileOrTablet: false, isSmallDesktop: true, isLargeDesktop: false };
      } else if (isXL && state.currentSize !== SCREEN_SIZE.XL) {
        updatedState = { currentSize: SCREEN_SIZE.XL, isMobile: false, isTablet: false, isMobileOrTablet: false, isSmallDesktop: false, isLargeDesktop: true };
      }

      if (updatedState) {
        dispatch({
          type: 'SET_SCREEN_SIZE',
          payload: updatedState
        });
      }
    }, [isXS, isSM, isMD, isLG, isXL, state]);

  return (
    <ScreenSizeContext.Provider value={state}>
      {props.children}
    </ScreenSizeContext.Provider>
  )
};